import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { tasksAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  CheckSquare,
  Plus,
  Clock,
  AlertTriangle,
  CheckCircle,
  Calendar,
  FileText,
  User,
  Sparkles,
  Loader2,
  Trash2,
  Phone,
  Mail,
  RefreshCw,
  ArrowRight,
  Zap,
  Bot,
  Send,
} from 'lucide-react';

const TASK_CATEGORIES = [
  { id: 'relance_client', name: 'Relance client', color: '#f97316' },
  { id: 'finaliser_devis', name: 'Finaliser devis', color: '#3b82f6' },
  { id: 'envoyer_devis', name: 'Envoyer devis', color: '#22c55e' },
  { id: 'pieces_manquantes', name: 'Pièces manquantes', color: '#ef4444' },
  { id: 'suivi_financement', name: 'Suivi financement', color: '#8b5cf6' },
  { id: 'rdv', name: 'RDV / Visite', color: '#06b6d4' },
  { id: 'appel', name: 'Appel téléphonique', color: '#eab308' },
  { id: 'email', name: 'Email à envoyer', color: '#ec4899' },
  { id: 'autre', name: 'Autre', color: '#64748b' },
];

const TasksPage = () => {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [summary, setSummary] = useState({});
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [autoProcessLoading, setAutoProcessLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [filter, setFilter] = useState('all'); // all, a_faire, en_cours, termine, overdue
  const [categoryFilter, setCategoryFilter] = useState('all'); // Filter by task category
  
  const [form, setForm] = useState({
    title: '',
    description: '',
    task_type: 'relance_client',
    priority: 'normale',
    due_date: '',
    due_time: '',
    devis_id: '',
    devis_reference: '',
    client_id: '',
    client_name: '',
  });

  useEffect(() => {
    fetchData();
  }, [filter, categoryFilter]);

  const fetchData = async () => {
    try {
      const params = {};
      if (filter === 'overdue') {
        params.overdue = true;
      } else if (filter !== 'all') {
        params.status = filter;
      }
      if (categoryFilter !== 'all') {
        params.task_type = categoryFilter;
      }
      
      const [tasksRes, summaryRes] = await Promise.all([
        tasksAPI.getAll(params),
        tasksAPI.getSummary(),
      ]);
      
      setTasks(tasksRes.data || []);
      setSummary(summaryRes.data || {});
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuggestions = async () => {
    setSuggestionsLoading(true);
    try {
      const response = await tasksAPI.getSuggestions();
      setSuggestions(response.data || []);
      if (response.data?.length === 0) {
        toast.info('Aucune suggestion pour le moment. Tous vos devis sont à jour !');
      }
    } catch (error) {
      toast.error('Erreur lors de la génération des suggestions');
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const handleAutoProcess = async () => {
    setAutoProcessLoading(true);
    try {
      const isAdmin = user?.role === 'admin';
      const response = isAdmin 
        ? await tasksAPI.autoProcess()
        : await tasksAPI.autoProcessSingle();
      
      const data = response.data;
      
      if (isAdmin) {
        if (data.total_tasks_created > 0) {
          toast.success(
            `${data.total_tasks_created} tâche(s) créée(s) automatiquement. ${data.notifications_sent} email(s) envoyé(s) aux vendeurs.`,
            { duration: 5000 }
          );
        } else {
          toast.info('Aucune nouvelle tâche à créer. Tous les devis sont suivis !');
        }
      } else {
        if (data.tasks_created > 0) {
          toast.success(
            `${data.tasks_created} tâche(s) créée(s) automatiquement.${data.notification_sent ? ' Un email vous a été envoyé.' : ''}`,
            { duration: 5000 }
          );
        } else {
          toast.info('Aucune nouvelle tâche à créer. Vos devis sont tous suivis !');
        }
      }
      
      fetchData();
      setSuggestions([]);
    } catch (error) {
      console.error('Auto process error:', error);
      if (error.response?.status === 403) {
        toast.error('Cette fonctionnalité est réservée aux administrateurs');
      } else {
        toast.error('Erreur lors de la création automatique des tâches');
      }
    } finally {
      setAutoProcessLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      const data = {
        ...form,
        due_date: form.due_date + (form.due_time ? `T${form.due_time}:00` : 'T09:00:00'),
      };
      
      if (editingTask) {
        await tasksAPI.update(editingTask.id, data);
        toast.success('Tâche mise à jour');
      } else {
        await tasksAPI.create(data);
        toast.success('Tâche créée');
      }
      
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    } finally {
      setFormLoading(false);
    }
  };

  const handleComplete = async (taskId) => {
    try {
      await tasksAPI.complete(taskId);
      toast.success('Tâche terminée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDelete = async (taskId) => {
    if (!window.confirm('Supprimer cette tâche ?')) return;
    
    try {
      await tasksAPI.delete(taskId);
      toast.success('Tâche supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleEdit = (task) => {
    setEditingTask(task);
    const dueDate = task.due_date?.split('T')[0] || '';
    const dueTime = task.due_date?.split('T')[1]?.substring(0, 5) || '';
    setForm({
      title: task.title || '',
      description: task.description || '',
      task_type: task.task_type || 'rappel',
      priority: task.priority || 'normale',
      due_date: dueDate,
      due_time: dueTime,
      devis_id: task.devis_id || '',
      devis_reference: task.devis_reference || '',
      client_id: task.client_id || '',
      client_name: task.client_name || '',
    });
    setDialogOpen(true);
  };

  const handleCreateFromSuggestion = async (suggestion) => {
    try {
      await tasksAPI.createFromSuggestion(suggestion);
      toast.success('Tâche créée depuis la suggestion');
      // Remove from suggestions list
      setSuggestions(prev => prev.filter(s => s.devis_id !== suggestion.devis_id));
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la création');
    }
  };

  const resetForm = () => {
    setEditingTask(null);
    setForm({
      title: '',
      description: '',
      task_type: 'rappel',
      priority: 'normale',
      due_date: '',
      due_time: '',
      devis_id: '',
      devis_reference: '',
      client_id: '',
      client_name: '',
    });
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      basse: 'bg-slate-100 text-slate-600',
      normale: 'bg-blue-100 text-blue-700',
      haute: 'bg-orange-100 text-orange-700',
      urgente: 'bg-red-100 text-red-700',
    };
    return <Badge className={styles[priority] || styles.normale}>{priority}</Badge>;
  };

  const getTypeBadge = (type) => {
    const types = {
      rappel: { label: 'Rappel', icon: Clock },
      relance: { label: 'Relance', icon: RefreshCw },
      appel: { label: 'Appel', icon: Phone },
      email: { label: 'Email', icon: Mail },
      rdv: { label: 'RDV', icon: Calendar },
      autre: { label: 'Autre', icon: CheckSquare },
    };
    const t = types[type] || types.autre;
    return (
      <Badge variant="outline" className="text-xs">
        <t.icon className="w-3 h-3 mr-1" />
        {t.label}
      </Badge>
    );
  };

  const isOverdue = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate) < new Date() && new Date(dueDate).toDateString() !== new Date().toDateString();
  };

  const isDueToday = (dueDate) => {
    if (!dueDate) return false;
    return new Date(dueDate).toDateString() === new Date().toDateString();
  };

  const formatDueDate = (dueDate) => {
    if (!dueDate) return '';
    const date = new Date(dueDate);
    const today = new Date();
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);
    
    if (date.toDateString() === today.toDateString()) {
      return `Aujourd'hui ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (date.toDateString() === tomorrow.toDateString()) {
      return `Demain ${date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}`;
    }
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const formatPrice = (price) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(price || 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-800"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tasks-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
            Tâches
          </h1>
          <p className="text-slate-500 mt-1">
            {summary.total_pending || 0} tâches en attente
            {summary.overdue > 0 && <span className="text-red-500 ml-2">• {summary.overdue} en retard</span>}
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button 
            variant="outline"
            onClick={fetchSuggestions}
            disabled={suggestionsLoading}
            data-testid="suggest-btn"
          >
            {suggestionsLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2 text-yellow-500" />}
            Suggestions IA
          </Button>
          <Button 
            variant="outline"
            onClick={handleAutoProcess}
            disabled={autoProcessLoading}
            className="border-green-200 hover:bg-green-50"
            data-testid="auto-process-btn"
          >
            {autoProcessLoading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Bot className="w-4 h-4 mr-2 text-green-600" />
            )}
            {user?.role === 'admin' ? 'Auto-créer toutes' : 'Auto-créer mes tâches'}
          </Button>
          <Button 
            className="bg-slate-800 hover:bg-slate-900"
            onClick={() => setDialogOpen(true)}
            data-testid="new-task-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouvelle tâche
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('a_faire')}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{summary.a_faire || 0}</p>
              <p className="text-xs text-slate-500">À faire</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('overdue')}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{summary.overdue || 0}</p>
              <p className="text-xs text-slate-500">En retard</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('all')}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-orange-100 rounded-lg">
              <Calendar className="w-5 h-5 text-orange-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{summary.due_today || 0}</p>
              <p className="text-xs text-slate-500">Aujourd'hui</p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-slate-200 cursor-pointer hover:shadow-md transition-shadow" onClick={() => setFilter('termine')}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{summary.en_cours || 0}</p>
              <p className="text-xs text-slate-500">En cours</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* AI Suggestions */}
      {suggestions.length > 0 && (
        <Card className="border-yellow-200 bg-yellow-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-yellow-500" />
              Suggestions IA ({suggestions.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {suggestions.slice(0, 5).map((suggestion, index) => (
                <div key={index} className="flex items-start justify-between gap-4 p-3 bg-white rounded-lg border border-yellow-200">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      {getPriorityBadge(suggestion.priority)}
                      <span className="text-sm font-medium text-slate-800">{suggestion.title}</span>
                    </div>
                    <p className="text-xs text-slate-500 mb-2">{suggestion.reason}</p>
                    <div className="flex items-center gap-3 text-xs text-slate-500">
                      <Link to={`/admin/devis/${suggestion.devis_id}`} className="hover:text-blue-600 flex items-center gap-1">
                        <FileText className="w-3 h-3" />
                        {suggestion.devis_reference}
                      </Link>
                      <span className="flex items-center gap-1">
                        <User className="w-3 h-3" />
                        {suggestion.client_name}
                      </span>
                      <span className="font-mono">{formatPrice(suggestion.devis_total)}</span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    className="bg-yellow-500 hover:bg-yellow-600 text-slate-900"
                    onClick={() => handleCreateFromSuggestion(suggestion)}
                  >
                    <Zap className="w-3 h-3 mr-1" />
                    Créer
                  </Button>
                </div>
              ))}
              {suggestions.length > 5 && (
                <p className="text-sm text-center text-slate-500">+{suggestions.length - 5} autres suggestions</p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tasks List */}
      <div className="flex flex-wrap items-center gap-4 mb-4">
        <Tabs value={filter} onValueChange={setFilter} className="flex-1">
          <TabsList>
            <TabsTrigger value="all">Toutes</TabsTrigger>
            <TabsTrigger value="a_faire">À faire</TabsTrigger>
            <TabsTrigger value="en_cours">En cours</TabsTrigger>
            <TabsTrigger value="overdue">En retard</TabsTrigger>
            <TabsTrigger value="termine">Terminées</TabsTrigger>
          </TabsList>
        </Tabs>
        
        {/* Category filter */}
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[200px]">
            <SelectValue placeholder="Toutes catégories" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes catégories</SelectItem>
            {TASK_CATEGORIES.map((cat) => (
              <SelectItem key={cat.id} value={cat.id}>
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: cat.color }} />
                  {cat.name}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {tasks.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              Aucune tâche {filter !== 'all' && `avec le statut "${filter}"`}
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {tasks.map((task) => {
                const category = TASK_CATEGORIES.find(c => c.id === task.task_type) || TASK_CATEGORIES[TASK_CATEGORIES.length - 1];
                return (
                  <div
                    key={task.id}
                    className={`p-4 hover:bg-slate-50 transition-colors ${task.status === 'termine' ? 'opacity-60' : ''}`}
                    data-testid={`task-${task.id}`}
                  >
                    <div className="flex items-start gap-4">
                      <Checkbox
                        checked={task.status === 'termine'}
                        onCheckedChange={() => task.status !== 'termine' && handleComplete(task.id)}
                        className="mt-1"
                        data-testid={`task-checkbox-${task.id}`}
                      />
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          {/* Category badge */}
                          <Badge 
                            className="text-white text-xs"
                            style={{ backgroundColor: category.color }}
                          >
                            {category.name}
                          </Badge>
                          <span className={`font-medium ${task.status === 'termine' ? 'line-through text-slate-400' : 'text-slate-800'}`}>
                            {task.title}
                          </span>
                            {task.is_ai_suggested && (
                              <Badge variant="outline" className="text-yellow-600 border-yellow-300">
                                <Sparkles className="w-3 h-3 mr-1" />
                                IA
                              </Badge>
                            )}
                            {getPriorityBadge(task.priority)}
                            {getTypeBadge(task.task_type)}
                          </div>
                          
                          {task.description && (
                            <p className="text-sm text-slate-500 mb-2">{task.description}</p>
                          )}
                          
                          <div className="flex items-center gap-4 text-xs text-slate-500 flex-wrap">
                            <span className={`flex items-center gap-1 ${isOverdue(task.due_date) ? 'text-red-500 font-medium' : isDueToday(task.due_date) ? 'text-orange-500' : ''}`}>
                              <Calendar className="w-3 h-3" />
                              {formatDueDate(task.due_date)}
                              {isOverdue(task.due_date) && ' (en retard)'}
                            </span>
                            
                            {task.devis_reference && (
                              <Link to={`/admin/devis/${task.devis_id}`} className="flex items-center gap-1 hover:text-blue-600">
                                <FileText className="w-3 h-3" />
                                {task.devis_reference}
                              </Link>
                            )}
                            
                            {task.client_name && (
                              <span className="flex items-center gap-1">
                                <User className="w-3 h-3" />
                                {task.client_name}
                              </span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-1">
                          {task.status !== 'termine' && (
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-8 text-green-600 border-green-300 hover:bg-green-50 hover:text-green-700"
                              onClick={() => handleComplete(task.id)}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Fait
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleEdit(task)}
                          >
                            <ArrowRight className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500 hover:text-red-600"
                            onClick={() => handleDelete(task.id)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

      {/* New/Edit Task Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              {editingTask ? 'Modifier la tâche' : 'Nouvelle tâche'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="title">Titre *</Label>
              <Input
                id="title"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Ex: Relancer le client, Envoyer le devis..."
                required
                data-testid="task-title-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="task_type">Type</Label>
                <Select value={form.task_type} onValueChange={(v) => setForm({ ...form, task_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rappel">Rappel</SelectItem>
                    <SelectItem value="relance">Relance</SelectItem>
                    <SelectItem value="appel">Appel</SelectItem>
                    <SelectItem value="email">Email</SelectItem>
                    <SelectItem value="rdv">RDV</SelectItem>
                    <SelectItem value="autre">Autre</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="priority">Priorité</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="basse">Basse</SelectItem>
                    <SelectItem value="normale">Normale</SelectItem>
                    <SelectItem value="haute">Haute</SelectItem>
                    <SelectItem value="urgente">Urgente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="due_date">Date d'échéance *</Label>
                <Input
                  id="due_date"
                  type="date"
                  value={form.due_date}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                  required
                  data-testid="task-due-date-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="due_time">Heure</Label>
                <Input
                  id="due_time"
                  type="time"
                  value={form.due_time}
                  onChange={(e) => setForm({ ...form, due_time: e.target.value })}
                />
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                placeholder="Détails supplémentaires..."
              />
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button
                type="submit"
                className="bg-slate-800 hover:bg-slate-900"
                disabled={formLoading}
                data-testid="save-task-btn"
              >
                {formLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {editingTask ? 'Mettre à jour' : 'Créer'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default TasksPage;
